/**
 * 🎞 **띠(스트립) — 몇 장인가·어떻게 넘기나.** 세 화면(책 뷰어·카메라 AR·**관리자 미리보기**)이 쓴다.
 *
 * 🔴 원래 `apps/web/src/book/stripStyle.ts` 에 있었다 — 그래서 관리자 미리보기는 띠를 **못 돌렸다**:
 * 12장짜리 꼬투리가 가느다란 띠 한 줄로 납작하게 보였다(2026-09-11). 운영자는 게시 전에 그 화면을 본다.
 * 판정은 shared 한 곳(CLAUDE.md 「미리보기는 렌더러와 같은 판정 함수」).
 */
/**
 * 🎞 **스트립 재생 스타일 — 책 뷰어와 카메라 AR 이 같은 것을 쓴다.**
 *
 * 스트립 = 정사각 프레임을 가로로 이어 붙인 한 장. `width: N×100%` 로 늘려 놓고 `translateX` 를
 * `steps(N)` 으로 한 칸씩 넘긴다(키프레임은 CSS — `magic-strip`/`armagic-strip`, 둘 다 끝을
 * `--strip-end` 로 받는다).
 *
 * ## 반복 vs 한 번만
 * - 반복(기본): 끝이 -100% — 다음 바퀴의 첫 장과 같은 자리라 매끄럽게 돈다.
 * - **한 번만(`once`)**: 빵 한 입처럼 되돌아가면 안 되는 동작. 끝이 -100% 면 **빈 칸에서 멈춘다.**
 *   그래서 끝을 **마지막 장**(-(N-1)/N)으로 옮기고 `steps(N-1)`·`forwards` 로 멈춘다.
 *   보이기 전에 다 돌아 버리지 않게, 부르는 쪽이 보일 때 `<img>` 를 새로 붙인다(key).
 *
 * 🔴 여기 말고 화면에서 따로 짜면 「책에선 한 번 먹고 멈추는데 카메라에선 계속 먹는다」가 된다.
 */
export const STRIP_FPS_MIN = 1;
export const STRIP_FPS_MAX = 12; // 시각 안전 — 한 칸 넘김이 초당 12를 넘지 않는다(번쩍임 아님)
export const STRIP_FPS_DEFAULT = 6; // 책 뷰어가 쓰던 값 그대로 — fps 를 안 적은 옛 스트립이 갑자기 느려지지 않게

/** 화면이 인라인 스타일로 쓰는 값 — React 타입에 묶지 않는다(관리자·책·카메라 셋이 쓴다). */
export type StripStyle = Record<string, string | number>;

export function stripStyle(frames: number, fps: number | undefined, once: boolean | undefined): StripStyle {
  if (frames <= 1) return {};
  const f = Math.max(STRIP_FPS_MIN, Math.min(STRIP_FPS_MAX, fps ?? STRIP_FPS_DEFAULT));
  if (once) {
    const steps = Math.max(1, frames - 1);
    return {
      width: `${frames * 100}%`,
      animationDuration: `${steps / f}s`,
      animationTimingFunction: `steps(${steps})`,
      animationIterationCount: 1,
      animationFillMode: "forwards",
      "--strip-end": `-${((frames - 1) / frames) * 100}%`,
    };
  }
  return {
    width: `${frames * 100}%`,
    animationDuration: `${frames / f}s`,
    animationTimingFunction: `steps(${frames})`,
  };
}

/**
 * 🔄 **띠의 한 장을 골라 보여 준다** — 끌어 돌리는 층(`spin`)용. 시간으로 넘기지 않으니 애니메이션을 끈다.
 * 세 화면이 같은 값을 쓴다(previewParity).
 */
export function stripFrameStyle(frames: number, frame: number): StripStyle {
  if (frames <= 1) return {};
  const k = Math.min(Math.max(0, Math.round(frame)), frames - 1);
  return {
    width: `${frames * 100}%`,
    transform: `translateX(-${(k / frames) * 100}%)`,
    animation: "none",
  };
}

/**
 * 얼마나 어긋나도 **띠**로 볼까. 우리가 굽는 띠는 언제나 **정확히** N배다(6.000·8.000).
 * 0.1 이면 2장짜리는 1.90~2.10 만 띠로 본다.
 */
export const STRIP_ASPECT_TOL = 0.1;

/**
 * 스트립 장 수 — 가로÷세로. 정지 그림은 1. 정사각이 아닌 그림은 스트립이 아니다.
 *
 * 🔴 **가로로 길쭉한 한 장을 띠로 오해하면 조용히 반쪽만 나온다** (2026-09-10).
 * 오려 온 종이 고래는 627×368(=1.70)이었다. 그냥 반올림하면 **2장짜리 띠**가 되어
 * `object-fit: fill` 로 늘어난 채 **왼쪽 절반만** 보이고, 그마저 옆으로 넘어갔다.
 * 에러는 하나도 안 났다 — 화면만 이상했다. 그래서 **정사각의 배수에 가까울 때만** 띠로 본다.
 */
export function stripFrames(naturalWidth: number, naturalHeight: number): number {
  if (!(naturalHeight > 0)) return 1;
  const ratio = naturalWidth / naturalHeight;
  const n = Math.max(1, Math.round(ratio));
  if (n === 1) return 1;
  return Math.abs(ratio - n) <= STRIP_ASPECT_TOL ? n : 1;
}
